import type { PredictionMarketChoice, PredictionMarketSnapshot } from "@roman/shared/types/PredictionMarket";

export interface Choice {
  id: string;
  name: string;
  latestProbability?: number;
}

export interface ExtractedChoices {
  type: PredictionMarketType;
  choices: Choice[];
  sortedChoices: Choice[];
}

export const extractChoices = (predictionMarketHistory: PredictionMarketSnapshot[]): ExtractedChoices => {
  if (!predictionMarketHistory || predictionMarketHistory.length === 0) {
    return { type: PredictionMarketType.MultipleChoice, choices: [], sortedChoices: [] };
  }

  const firstSnapshot = predictionMarketHistory[0];
  if (!firstSnapshot?.predictionChoiceStates) {
    return { type: PredictionMarketType.MultipleChoice, choices: [], sortedChoices: [] };
  }

  // Get the latest snapshot for current probabilities
  const latestSnapshot = predictionMarketHistory[predictionMarketHistory.length - 1];
  const latestProbabilities = new Map<string, number>();

  if (latestSnapshot?.predictionChoiceStates) {
    const normalizedProbabilities = normalizeChoicesPercentage(latestSnapshot.predictionChoiceStates);
    for (const state of normalizedProbabilities) {
      latestProbabilities.set(state.choiceId, state.probability);
    }
  }

  const choices = firstSnapshot.predictionChoiceStates.map((state) => ({
    id: state.choiceId,
    name: state.choiceName,
    latestProbability: latestProbabilities.get(state.choiceId),
  }));

  // Sort choices by latestProbability in descending order if there are 3 or more
  const sortedChoices =
    choices.length >= 3
      ? [...choices].sort((a, b) => {
          const probA = a.latestProbability ?? 0;
          const probB = b.latestProbability ?? 0;
          return probB - probA;
        })
      : choices;

  const type = getPredictionMarketType(choices);

  return { type, choices, sortedChoices };
};

export enum PredictionMarketType {
  MultipleChoice = "MultipleChoice",
  TwoChoice = "TwoChoice",
}

export const getPredictionMarketType = (
  choices: Omit<PredictionMarketChoice, "probability" | "id">[],
): PredictionMarketType => {
  if (choices.length < 2) {
    throw new Error("There must be at least 2 choices");
  }
  if (choices.length === 2) {
    return PredictionMarketType.TwoChoice;
  }
  return PredictionMarketType.MultipleChoice;
};

export const getYesNoChoices = <T extends PredictionMarketChoice>(
  choices: T[],
): {
  yesChoice: T;
  noChoice: T;
} => {
  const yesChoice = choices[0];
  const noChoice = choices[1];
  if (!yesChoice || !noChoice) {
    throw new Error("There must be Yes and No choices");
  }
  return { yesChoice, noChoice };
};

export const normalizeChoicesPercentage = <T extends { probability: number }>(choices: T[], precision = 0): T[] => {
  const percentages = choices.map((c) => c.probability * 100);
  const normalized = normalizePercentagesTo100(percentages, precision);

  return choices.map((c, i) => ({
    ...c,
    probability: normalized[i] / 100,
  }));
};

/**
 * Normalizes an array of percentages so that the sum is exactly 100%.
 * Uses the Largest Remainder Method with zero-preservation.
 *
 * @param values - Array of non-negative numbers representing percentages.
 * @param precision - Number of decimal places to round to (default is 0).
 * @returns A new array of rounded percentages summing exactly to 100.
 */
export const normalizePercentagesTo100 = (values: number[], precision = 0): number[] => {
  const factor = 10 ** precision;
  const scaled = values.map((v) => v * factor);
  const rounded = scaled.map(Math.floor);
  const totalRounded = rounded.reduce((sum, v) => sum + v, 0);
  const missing = 100 * factor - totalRounded;

  if (missing === 0) {
    return rounded.map((v) => v / factor);
  }

  // Calculate remainders, ignore zeros to prevent ghost probabilities
  const remainders = values
    .map((_, i) => ({
      index: i,
      remainder: scaled[i] - rounded[i],
    }))
    .filter((v) => values[v.index] > 0);

  remainders.sort((a, b) => b.remainder - a.remainder);

  // Distribute the missing units to the highest remainders
  for (let i = 0; i < missing; i++) {
    const idx = remainders[i % remainders.length].index;
    rounded[idx] += 1;
  }

  return rounded.map((v) => v / factor);
};
